import React, { useRef } from "react";
import { VerificationInputFieldContainer } from "./styles";

const VerificationInputField = ({
  className,
  title,
  titlePosition,
  width,
  required,
  length,
  labelColor,
  labelSize,
  labelWeight,
  inputFontSize,
  inputFontWeight,
  inputColor,
  inputBackgroundColor,
  borderColor,
  inputHeight,
  inputWidth,
  margin,
  error,
  errorMessage,
  success,
  values,
  setValues,
}) => {
  const inputsRef = useRef([]);

  const handleChange = (e, index) => {
    const { value } = e.target;
    const newValues = [...values];

    // Handle multi-character input (paste or autofill fallback)
    if (value.length > 1) {
      // If overwriting, take the last character typed
      const previousValue = values[index] || "";
      const targetChar = value.split("").find(char => char !== previousValue) || value.slice(-1);
      
      // if need character support, change regex to /^[a-zA-Z0-9]$/
      if (/^[0-9]$/.test(targetChar)) {
        newValues[index] = targetChar.toUpperCase();
        setValues(newValues);
        if (index < length - 1) {
          inputsRef.current[index + 1]?.focus();
        }
      }
      return;
    }

    // Handle single character or empty string (deletion)
    // if need character support, change regex to /^[a-zA-Z0-9]$/
    if (/^[0-9]$/.test(value) || value === "") {
      newValues[index] = value.toUpperCase();
      setValues(newValues);
      
      // Auto-focus next field if a character was entered
      if (value !== "" && index < length - 1) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim();
    const sanitized = pasteData.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    
    if (!sanitized) return;

    const newValues = [...values];
    for (let i = 0; i < length; i++) {
      newValues[i] = sanitized[i] || "";
    }
    setValues(newValues);

    const nextIndex = Math.min(sanitized.length, length) - 1;
    if (nextIndex >= 0) {
      inputsRef.current[nextIndex]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newValues = [...values];
      
      if (values[index] !== "") {
        // Clear current cell first
        newValues[index] = "";
        setValues(newValues);
      } else if (index > 0) {
        // If already empty, clear previous cell and focus it
        newValues[index - 1] = "";
        setValues(newValues);
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  return (
    <VerificationInputFieldContainer
      className={className}
      titlePosition={titlePosition}
      labelColor={labelColor}
      labelSize={labelSize}
      labelWeight={labelWeight}
      borderColor={borderColor}
      inputHeight={inputHeight}
      inputWidth={inputWidth}
      inputColor={inputColor}
      inputFontSize={inputFontSize}
      inputFontWeight={inputFontWeight}
      inputBackgroundColor={inputBackgroundColor}
      success={success}
      width={width}
      margin={margin}
      error={error}
    >
      {title && (
        <label>
          {title}
          {required && <span className="required"> *</span>}
        </label>
      )}
      <div className="blockContainer">
        {Array.from({ length }).map((_, idx) => (
          <input
            key={idx}
            type="text"
            maxLength={2} // Changed to 2 to catch overwrite events seamlessly
            value={values[idx] || ""}
            onChange={(e) => handleChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onPaste={handlePaste}
            ref={(el) => (inputsRef.current[idx] = el)}
          />
        ))}
      </div>
      {error && <div className="warning">{errorMessage}</div>}
    </VerificationInputFieldContainer>
  );
};

export default VerificationInputField;